import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { firestore, storage } from '../firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, addDoc,
  query, where, onSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { matchTeamByPasscodeForEvent, TEAM_PASSCODE_MAP_NO_TEAM } from '../utils/matchTeamByPasscode';
import './VideoSubmissionPuzzle.css';

const MAX_DURATION_SECONDS = 30;
const MAX_FILE_BYTES = 300 * 1024 * 1024;

function assignmentDocId(eventId, puzzleId, teamId) {
  return `${eventId}_${puzzleId}_${teamId}`;
}

async function pickBalancedSponsors(eventId, puzzleId, eligibleSponsors, count) {
  if (eligibleSponsors.length <= count) return eligibleSponsors.map((s) => s.id);

  const snap = await getDocs(
    query(collection(firestore, 'videoAssignments'), where('puzzleId', '==', puzzleId))
  );
  const countBySponsor = {};
  eligibleSponsors.forEach((s) => { countBySponsor[s.id] = 0; });
  snap.docs.forEach((d) => {
    const ids = d.data().sponsorIds || [];
    ids.forEach((id) => {
      if (countBySponsor[id] !== undefined) countBySponsor[id]++;
    });
  });

  const sorted = eligibleSponsors
    .map((s) => ({ id: s.id, count: countBySponsor[s.id] }))
    .sort((a, b) => a.count - b.count || Math.random() - 0.5);

  return sorted.slice(0, count).map((s) => s.id);
}

async function validateVideoFile(file) {
  if (file.size > MAX_FILE_BYTES) {
    return `File is too large (${(file.size / 1024 / 1024).toFixed(0)} MB). Maximum is 300 MB.`;
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > MAX_DURATION_SECONDS) {
        resolve(`Video is ${Math.round(video.duration)}s long. Maximum is ${MAX_DURATION_SECONDS}s.`);
      } else {
        resolve(null);
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    video.src = url;
  });
}

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'Pending review', className: 'vsp-badge vsp-badge--pending' },
    approved: { label: 'Approved', className: 'vsp-badge vsp-badge--approved' },
    rejected: { label: 'Needs revision', className: 'vsp-badge vsp-badge--rejected' },
  };
  const { label, className } = map[status] || { label: status, className: 'vsp-badge' };
  return <span className={className}>{label}</span>;
};

const SponsorCard = ({ sponsor, submission, onUpload, uploading }) => {
  const fileInputRef = useRef(null);
  const recordInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [orientationWarning, setOrientationWarning] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [requestingPermissions, setRequestingPermissions] = useState(false);

  const isLocked = submission?.status === 'approved';
  const isPending = submission?.status === 'pending';
  const isRejected = submission?.status === 'rejected';
  const hasSubmission = !!submission;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setValidationError('');
    setOrientationWarning(false);

    const err = await validateVideoFile(file);
    if (err) {
      setValidationError(err);
      setSelectedFile(null);
      return;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.videoWidth > 0 && video.videoHeight > 0 && video.videoWidth > video.videoHeight) {
        setOrientationWarning(true);
      }
    };
    video.src = url;

    setSelectedFile(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    onUpload(sponsor.id, selectedFile);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (recordInputRef.current) recordInputRef.current.value = '';
  };

  const handleRecordNow = async () => {
    setPermissionError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      // Continue to iOS camera flow even if this API is unavailable.
      if (recordInputRef.current) recordInputRef.current.click();
      return;
    }

    try {
      setRequestingPermissions(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach((track) => track.stop());
      if (recordInputRef.current) recordInputRef.current.click();
    } catch (err) {
      const isPermissionIssue =
        err?.name === 'NotAllowedError' ||
        err?.name === 'PermissionDeniedError' ||
        err?.name === 'SecurityError';
      setPermissionError(
        isPermissionIssue
          ? 'Camera/microphone permission is blocked. On iPhone: open Settings for your browser app (Safari/Chrome) and allow Camera + Microphone, then try again.'
          : `Could not access camera/microphone: ${err?.message || String(err)}`
      );
    } finally {
      setRequestingPermissions(false);
    }
  };

  return (
    <div className={`vsp-card ${isLocked ? 'vsp-card--locked' : ''}`}>
      <div className="vsp-card-header">
        {sponsor.logo && (
          <img src={sponsor.logo} alt={sponsor.name} className="vsp-card-logo" />
        )}
        <div>
          <div className="vsp-card-name">{sponsor.name}</div>
          {hasSubmission && <StatusBadge status={submission.status} />}
        </div>
      </div>

      <p className="vsp-card-task">
        Record a short thank-you video (max {MAX_DURATION_SECONDS}s, vertical) mentioning <strong>{sponsor.name}</strong> as a Fresh Meet sponsor.
      </p>

      {submission?.judgeNotes && isRejected && (
        <div className="vsp-judge-notes">
          <strong>Feedback:</strong> {submission.judgeNotes}
        </div>
      )}

      {isLocked ? (
        <div className="vsp-locked-msg">
          Video approved — {submission.basePoints + (submission.creativityPoints || 0)} pts earned
        </div>
      ) : isPending ? (
        <div className="vsp-pending-msg">Your video is under review. Check back soon!</div>
      ) : (
        <form onSubmit={handleSubmit} className="vsp-upload-form">
          <div className="vsp-file-row">
            <label className="vsp-file-label">
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="vsp-file-input"
              />
              <span className="vsp-file-btn">Choose video</span>
            </label>
            <label className="vsp-file-label">
              <input
                ref={recordInputRef}
                type="file"
                accept="video/*"
                capture="environment"
                onChange={handleFileChange}
                className="vsp-file-input"
              />
              <button
                type="button"
                className="vsp-file-btn vsp-file-btn--record"
                onClick={handleRecordNow}
                disabled={requestingPermissions}
              >
                {requestingPermissions ? 'Opening camera…' : 'Record now'}
              </button>
            </label>
          </div>
          <div className="vsp-note">
            You can also record in your phone&apos;s Camera app and then tap <strong>Choose video</strong> to upload from your gallery/photos.
          </div>

          {selectedFile && (
            <>
              <div className="vsp-file-selected">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
              </div>
              <div className="vsp-submit-reminder">
                Recording saved on your phone, but not submitted yet. Tap <strong>Submit video</strong> below.
              </div>
            </>
          )}
          {orientationWarning && (
            <div className="vsp-warning">Tip: video looks landscape. Vertical (portrait) is preferred for social media.</div>
          )}
          {permissionError && (
            <div className="vsp-error">{permissionError}</div>
          )}
          {validationError && (
            <div className="vsp-error">{validationError}</div>
          )}

          {uploading != null && (
            <div className="vsp-progress-wrap">
              <div className="vsp-progress-bar" style={{ width: `${uploading}%` }} />
              <span className="vsp-progress-label">{uploading < 100 ? `Uploading… ${uploading}%` : 'Processing…'}</span>
              <p className="vsp-upload-notice">Keep this page open until upload completes.</p>
            </div>
          )}

          {uploading == null && (
            <button
              type="submit"
              className="btn-fm-primary vsp-submit-btn"
              disabled={!selectedFile}
            >
              {isRejected ? 'Resubmit video' : 'Submit video'}
            </button>
          )}
        </form>
      )}
    </div>
  );
};

const VideoSubmissionPuzzle = ({ puzzle, activeEvent }) => {
  const navigate = useNavigate();

  const [passcodeInput, setPasscodeInput] = useState('');
  const [team, setTeam] = useState(null);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [sponsors, setSponsors] = useState([]);
  const [assignedSponsorIds, setAssignedSponsorIds] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [setupLoading, setSetupLoading] = useState(false);

  const unsubRef = useRef(null);

  useEffect(() => {
    if (!activeEvent) return;
    let cancelled = false;
    (async () => {
      try {
        const merged = await fetchSponsorsMergedForEvent(activeEvent.id);
        if (cancelled) return;
        const eligible = merged.filter((s) => !!s.includeInVideoChallenge && !s.hidden);
        eligible.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setSponsors(eligible);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [activeEvent]);

  useEffect(() => {
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, []);

  const subscribeToSubmissions = (teamId) => {
    if (unsubRef.current) unsubRef.current();
    const q = query(
      collection(firestore, 'videoSubmissions'),
      where('puzzleId', '==', puzzle.id),
      where('teamId', '==', teamId)
    );
    unsubRef.current = onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.data().sponsorId] = { id: d.id, ...d.data() };
      });
      setSubmissions(map);
    });
  };

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      const { team: matchedTeam, status } = await matchTeamByPasscodeForEvent(
        activeEvent.id,
        passcodeInput
      );

      if (!matchedTeam) {
        setAuthError(
          status === TEAM_PASSCODE_MAP_NO_TEAM
            ? 'That passcode is on file, but there is no team record for that number in this puzzle’s event. ' +
              'Use Team Pairing for this event, or upload passcodes for the event this puzzle is tied to (see admin Bonus puzzle event).'
            : 'Incorrect passcode. Please try again.'
        );
        setAuthLoading(false);
        return;
      }

      setTeam(matchedTeam);
      setSetupLoading(true);

      try {
        // Get or create assignment
        const docId = assignmentDocId(activeEvent.id, puzzle.id, matchedTeam.id);
        const assignRef = doc(firestore, 'videoAssignments', docId);
        let assignSnap = await getDoc(assignRef);

        if (!assignSnap.exists()) {
          let eligible = sponsors;
          if (!eligible.length) {
            const merged = await fetchSponsorsMergedForEvent(activeEvent.id);
            eligible = merged.filter((s) => !!s.includeInVideoChallenge && !s.hidden);
            eligible.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
            setSponsors(eligible);
          }
          const picked = await pickBalancedSponsors(activeEvent.id, puzzle.id, eligible, 3);
          await setDoc(assignRef, {
            eventId: activeEvent.id,
            puzzleId: puzzle.id,
            teamId: matchedTeam.id,
            sponsorIds: picked,
            assignedAt: new Date(),
          });
          setAssignedSponsorIds(picked);
        } else {
          setAssignedSponsorIds(assignSnap.data().sponsorIds || []);
        }

        subscribeToSubmissions(matchedTeam.id);
      } catch (setupErr) {
        setTeam(null);
        setAuthError(
          'Could not set up your video challenge: ' + (setupErr.message || String(setupErr))
        );
      } finally {
        setSetupLoading(false);
      }
    } catch (err) {
      setAuthError('Error: ' + err.message);
    }
    setAuthLoading(false);
  };

  const handleUpload = async (sponsorId, file) => {
    if (!team) return;

    setUploadErrors((prev) => ({ ...prev, [sponsorId]: '' }));
    setUploading((prev) => ({ ...prev, [sponsorId]: 0 }));

    try {
      const ext = file.name.split('.').pop() || 'mp4';
      const storagePath = `videos/${activeEvent.id}/${puzzle.id}/${team.id}/${sponsorId}/video.${ext}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploading((prev) => ({ ...prev, [sponsorId]: pct }));
          },
          (err) => reject(err),
          () => resolve()
        );
      });

      const downloadUrl = await getDownloadURL(storageRef);
      const sponsor = sponsors.find((s) => s.id === sponsorId);

      // If there's an existing rejected submission, overwrite it by creating a new one
      // (old one stays in Firestore as history; judges see the latest pending one)
      await addDoc(collection(firestore, 'videoSubmissions'), {
        eventId: activeEvent.id,
        puzzleId: puzzle.id,
        teamId: team.id,
        sponsorId,
        sponsorName: sponsor?.name || '',
        videoUrl: downloadUrl,
        videoPath: storagePath,
        status: 'pending',
        basePoints: 0,
        creativityPoints: 0,
        judgeNotes: '',
        submittedAt: new Date(),
        reviewedAt: null,
        reviewedBy: null,
        bonusScoreId: null,
      });

      setUploading((prev) => {
        const next = { ...prev };
        delete next[sponsorId];
        return next;
      });
    } catch (err) {
      setUploading((prev) => {
        const next = { ...prev };
        delete next[sponsorId];
        return next;
      });
      const code = err?.code || '';
      const msg = err?.message || String(err);
      let detail = msg;
      if (code === 'storage/unauthorized' || /403|Forbidden/i.test(msg)) {
        detail =
          `${msg} If this is local dev, Storage may be blocked by App Check: copy the “App Check debug token” from the browser console and register it in Firebase Console → App Check → App → Manage debug tokens, or set REACT_APP_APPCHECK_DEBUG_TOKEN in .env to that UUID. You can temporarily set REACT_APP_DISABLE_APPCHECK=true only while enforcement is off in Firebase.`;
      }
      setUploadErrors((prev) => ({ ...prev, [sponsorId]: `Upload failed: ${detail}` }));
    }
  };

  const eventClosed = activeEvent?.isOpen === false;
  const puzzleDisabled = puzzle.enabled === false;

  if (!team) {
    return (
      <div className="bonus-puzzle-scoring">
        <img
          src="/fresh-meet-logo.png"
          alt="Fresh Meet Logo"
          className="fresh-meet-logo"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
        <h2>Puzzle {puzzle.number}</h2>
        <h3>{puzzle.title}</h3>
        <p>{puzzle.tagline}</p>
        {eventClosed && <div className="event-closed-banner"><p>Bonus puzzle scoring is closed for this event.</p></div>}
        {puzzleDisabled && <div className="event-closed-banner"><p>This puzzle is currently disabled.</p></div>}
        <form onSubmit={handlePasscodeSubmit} className="scoring-form">
          <fieldset disabled={eventClosed || puzzleDisabled}>
            <div className="form-group">
              <label>Team Passcode:</label>
              <input
                type="text"
                value={passcodeInput}
                onChange={(e) => setPasscodeInput(e.target.value)}
                placeholder="e.g. Meeple"
                required
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#64748b', textAlign: 'left' }}>
                Enter your team's passcode to continue.
              </small>
            </div>
            <button type="submit" className="btn-fm-primary" disabled={authLoading}>
              {authLoading ? 'Checking…' : 'Continue'}
            </button>
          </fieldset>
        </form>
        {authError && <p className="feedback feedback-error">{authError}</p>}
        <div className="bonus-return-home-wrap">
          <button type="button" onClick={() => navigate('/')} className="bonus-return-home-btn">
            Return to Main Page
          </button>
        </div>
      </div>
    );
  }

  if (setupLoading) {
    return (
      <div className="bonus-puzzle-scoring">
        <p>Setting up your video challenge…</p>
      </div>
    );
  }

  const assignedSponsors = (assignedSponsorIds || [])
    .map((id) => sponsors.find((s) => s.id === id))
    .filter(Boolean);

  // For display: use the most recent non-approved submission per sponsor
  // (approved ones are locked; pending/rejected show the active state)
  const activeSubmissionBySponsor = {};
  Object.values(submissions).forEach((sub) => {
    const existing = activeSubmissionBySponsor[sub.sponsorId];
    if (!existing || sub.submittedAt?.seconds > existing.submittedAt?.seconds) {
      activeSubmissionBySponsor[sub.sponsorId] = sub;
    }
  });

  return (
    <div className="bonus-puzzle-scoring vsp-root">
      <img
        src="/fresh-meet-logo.png"
        alt="Fresh Meet Logo"
        className="fresh-meet-logo"
        onClick={() => navigate('/')}
        style={{ cursor: 'pointer' }}
      />
      <h2>Puzzle {puzzle.number}</h2>
      <h3>{puzzle.title}</h3>
      <p>{puzzle.tagline}</p>
      <p className="vsp-intro">
        Record a short vertical thank-you video (max {MAX_DURATION_SECONDS}s) for each of your assigned sponsors below.
        Your videos will be reviewed by a judge. Once approved, you can't resubmit — so make them count!
      </p>

      {(eventClosed || puzzleDisabled) && (
        <div className="event-closed-banner">
          <p>{eventClosed ? 'Bonus puzzle scoring is closed for this event.' : 'This puzzle is currently disabled.'}</p>
        </div>
      )}

      <div className="vsp-cards">
        {assignedSponsors.length === 0 && (
          <p>No sponsors have been assigned. Please check back later or contact an organizer.</p>
        )}
        {assignedSponsors.map((sponsor) => (
          <SponsorCard
            key={sponsor.id}
            sponsor={sponsor}
            submission={activeSubmissionBySponsor[sponsor.id] || null}
            onUpload={handleUpload}
            uploading={uploading[sponsor.id] ?? null}
          />
        ))}
      </div>

      {Object.entries(uploadErrors).map(([sponsorId, err]) =>
        err ? <p key={sponsorId} className="feedback">{err}</p> : null
      )}

      <div className="bonus-return-home-wrap">
        <button type="button" onClick={() => navigate('/')} className="bonus-return-home-btn">
          Return to Main Page
        </button>
      </div>
    </div>
  );
};

export default VideoSubmissionPuzzle;
